import { Badge } from "@/components/ui/badge";
import { getReasonTranslation } from "@/pages/store-back-office/generate-term/view";
import { DataTableColumnHeader } from "@/pages/users/components/data-table-column-header";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { TermRequest } from "../data/types";
import { DataTableRowActions } from "./data-table-row-actions";

const statusConfig: Record<string, { label: string; variant: string }> = {
  APPROVED: { label: "Aprovado", variant: "bg-green-500 text-white" },
  REJECTED: { label: "Rejeitado", variant: "bg-red-500 text-white" },
  PENDING: { label: "Pendente", variant: "bg-yellow-500 text-white" },
  TERM_SIGNED: { label: "Assinado", variant: "bg-blue-500 text-white" },
  TERM_PENDING_SIGNATURE: { label: "Assinatura Pendente", variant: "bg-orange-500 text-white" },
  CANCELED: { label: "Cancelado", variant: "bg-gray-500 text-white" },
};

const getSLABadgeVariant = (
  slaRemaining:
    | {
        hours: number;
        minutes: number;
        expired: boolean;
        formatted_text: string;
      }
    | undefined
) => {
  if (!slaRemaining) return "bg-gray-500 text-white";
  if (slaRemaining.expired) return "bg-red-500 text-white";

  const totalMinutes = slaRemaining.hours * 60 + slaRemaining.minutes;
  const percentRemaining = (totalMinutes / 1440) * 100;

  if (percentRemaining > 70) return "bg-green-500 text-white";
  if (percentRemaining >= 30) return "bg-yellow-500 text-white";
  return "bg-red-500 text-white";
};

export const COLUMN_TO_DB_FIELD: Record<string, string> = {
  "Gerente Regional": "created_by",
  "Nome": "user_name",
  "Loja": "store_code",
  "Motivo": "reason",
  "Status": "status",
  "SLA": "sla_remaining",
  "Data da Solicitação": "created_at",
};

export const columns: ColumnDef<TermRequest>[] = [
  {
    id: "Gerente Regional",
    accessorKey: "created_by",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="Gerente Regional" />
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-center">{row.original.created_by}</div>
    ),
  },
  {
    id: "Nome",
    accessorKey: "user_name",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="Nome" />
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-center">{row.original.user_name}</div>
    ),
  },
  {
    id: "Loja",
    accessorKey: "store_code",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="Loja" />
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-center">{row.original.store_code}</div>
    ),
  },
  {
    id: "Motivo",
    accessorKey: "reason",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="Motivo" />
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-center">
        {getReasonTranslation(row.original.reason)}
      </div>
    ),
  },
  {
    id: "Status",
    accessorKey: "status",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="Status" />
      </div>
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      const config = statusConfig[status] || { label: status, variant: "bg-gray-500 text-white" };

      return (
        <div className="flex justify-center">
          <Badge
            className={config.variant}
            style={{ pointerEvents: "none" }}
          >
            {config.label}
          </Badge>
        </div>
      );
    },
  },
  {
    id: "SLA",
    accessorKey: "sla_remaining",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="SLA" />
      </div>
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      if (
        status === "APPROVED" ||
        status === "REJECTED" ||
        status === "TERM_SIGNED"
      ) {
        return <div className="text-center">-</div>;
      }

      const slaRemaining = row.original.sla_remaining;

      return (
        <div className="flex justify-center">
          <Badge
            className={getSLABadgeVariant(slaRemaining)}
            style={{ pointerEvents: "none" }}
          >
            {slaRemaining?.formatted_text || "N/A"}
          </Badge>
        </div>
      );
    },
  },
  {
    id: "Data da Solicitação",
    accessorKey: "created_at",
    header: ({ column }) => (
      <div className="flex justify-center">
        <DataTableColumnHeader column={column} title="Data da Solicitação" />
      </div>
    ),
    cell: ({ row }) => {
      const date = row.original.created_at;
      return (
        <div className="flex justify-center">
          {date ? format(new Date(date), "dd/MM/yyyy") : "-"}
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <div className="flex justify-center">
        <DataTableRowActions row={row} />
      </div>
    ),
  },
];